import classNames from "classnames";
import { addDays } from "date-fns";
import { ToolbarProps } from "elements/base/provider";
import { ToolbarGanttAddTaskIcon } from "elements/gantt/icons/gantt-add-task-icon";
import { getBaseTaskPosition, trackGanttEvent } from "elements/gantt/utils";
import { DropdownButton as DropdownButtonLegacy } from "frontend/canvas-designer-new/elements-toolbar/toolbar-buttons";
import {
  DropdownButton as DropdownButtonCIM,
  Icon,
} from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";
import { produceWithPatches } from "immer";
import { useRef, useState } from "react";
import { ButtonGroup } from "react-bootstrap";
import { GanttElement, Granularity } from "shared/datamodel/schemas";
import { placeTaskCard } from "shared/datamodel/task-card";
import { placeTask } from "shared/datamodel/task";
import { SupportedGanttElements } from "elements/gantt/types";
import { cleanDate, formatRelatedDates } from "shared/util/date-utils";
import style from "./gantt-toolbar.module.css";
import { useGanttMaxTasks } from "elements/gantt/hooks/gantt-max-tasks";
import { GanttToolbarDatePicker } from "./gantt-toolbar-date-picker";
import { CustomRows } from "elements/gantt/toolbar/custom-rows/custom-rows";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { IntegrationButton } from "elements/gantt/toolbar/integration-button/integration-btn";
import { useIsCIMReorderEnabled } from "utils/url-utils";

export default function GanttToolbar({ ids, elements, context }: ToolbarProps<GanttElement>) {
  const { reflect, undoRedoStack } = context;
  const [startDate, setStartDate] = useState<Date | undefined>(
    elements[0]?.startDate ? new Date(elements[0].startDate) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    elements[0]?.endDate ? new Date(elements[0].endDate) : undefined
  );
  const maxAllowedTasksInPlan = useGanttMaxTasks();
  const datePickerRef = useRef<HTMLDivElement>(null);
  const canGanttMondaySplitRows = useFeatureFlag("gantt-monday-split-rows");
  const useNewTaskElement = useFeatureFlag("new-task-element");
  const isCIM = useIsCIMReorderEnabled();
  const newFloatersStyle = useFeatureFlag("cim-new-floaters-style");
  const newStyle = isCIM && newFloatersStyle;
  const DropdownButton = newStyle ? DropdownButtonCIM : DropdownButtonLegacy;
  // Safety check: if no elements exist (e.g., during undo), render nothing
  // Must be after all hooks but before any other logic
  if (!elements || elements.length === 0 || !elements[0]) {
    return null;
  }

  function changeDate(start: Date | undefined, end: Date | undefined) {
    const numericStart = start?.getTime();
    const numericEnd = end?.getTime();
    const changes = ids.map((id, index) => {
      const element = elements[index];
      const [_, patch, inverse] = produceWithPatches(element, (draft) => {
        if (numericStart && numericEnd) {
          if (numericStart < numericEnd) {
            draft.startDate = numericStart;
            draft.endDate = numericEnd;
          }
        }

        if (numericStart && !numericEnd) {
          draft.startDate = numericStart;
          draft.endDate = numericStart;
        }

        if (numericEnd && !numericStart) {
          draft.startDate = numericEnd;
          draft.endDate = numericEnd;
        }
      });
      return { id, patch, inverse };
    });
    const doPatches = changes.filter(({ patch }) => patch.length > 0).map(({ id, patch }) => ({ id, patch }));
    if (!doPatches.length) {
      return;
    }
    const undoPatches = changes.map(({ id, inverse }) => ({ id, patch: inverse }));
    undoRedoStack?.addAction({
      do: () => {
        reflect?.mutate.patchCanvasEl({ changes: doPatches });
      },
      undo: () => {
        reflect?.mutate.patchCanvasEl({ changes: undoPatches });
      },
    });
  }

  function renderPicker() {
    return (
      <GanttToolbarDatePicker
        datePickerRef={datePickerRef}
        elements={elements}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        changeDate={changeDate}
        newStyle={newStyle}
      />
    );
  }

  function renderDateRange() {
    return <div className={style.title}>{formatRelatedDates(startDate, endDate)}</div>;
  }

  function renderGranularityTitle() {
    const text = granularityTitle(elements[0].granularity);
    const icon = newStyle ? (
      <Icon icon="icon-calendar-granularity-picker" />
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" viewBox="0 0 15 15">
        <rect width="14" height="14" x="14.5" y="0.5" stroke="#fff" rx="2.5" transform="rotate(90 14.5 .5)"></rect>
        <path stroke="#fff" d="M0 4.18h14.118"></path>
      </svg>
    );
    return (
      <div className={style.granularityTitle}>
        {icon}
        {text}
      </div>
    );
  }

  function changeGranularity(granularity: Granularity) {
    const changes = ids.map((id, index) => {
      const element = elements[index];
      const [_, patch, inverse] = produceWithPatches(element, (draft) => {
        draft.granularity = granularity;
      });
      return { id, patch, inverse };
    });
    const doPatches = changes.filter(({ patch }) => patch.length > 0).map(({ id, patch }) => ({ id, patch }));
    if (!doPatches.length) {
      return;
    }
    trackGanttEvent("gantt_granularity_changed");

    const undoPatches = changes.map(({ id, inverse }) => ({ id, patch: inverse }));
    undoRedoStack?.addAction({
      do: () => {
        reflect?.mutate.patchCanvasEl({ changes: doPatches });
      },
      undo: () => {
        reflect?.mutate.patchCanvasEl({ changes: undoPatches });
      },
    });
  }

  function granularityTitle(granularity: Granularity) {
    switch (granularity) {
      case "day": {
        return "Days";
      }
      case "week": {
        return "Weeks";
      }
      case "month": {
        return "Months";
      }
      case "quarter": {
        return "Quarter";
      }
    }
  }

  function renderGranularityPicker() {
    const granularities: Granularity[] = ["day", "week", "month", "quarter"];
    const selectedGranularity = elements[0].granularity;
    return (
      <div className={style.granularityPickerContainer}>
        {granularities.map((granularity) => (
          <div
            key={granularity}
            className={classNames(style.granularityPickerRow, {
              [style.selected]: granularity === selectedGranularity,
            })}
            onClick={() => changeGranularity(granularity)}
          >
            {newStyle ? (
              <>
                <div className={style.granularityPickerRowTitle}>{granularityTitle(granularity)}</div>
                <div className={style.granularityPickerCheckmark}>
                  <svg width="13" height="11" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 5.5L4.66667 10L12 1"
                      stroke="white"
                      strokeWidth="1.64033"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </>
            ) : (
              <>
                <div className={style.granularityPickerCheckmark}>
                  <svg width="13" height="11" viewBox="0 0 13 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M1 5.5L4.66667 10L12 1"
                      stroke="white"
                      strokeWidth="1.64033"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className={style.granularityPickerRowTitle}>{granularityTitle(granularity)}</div>
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  function createTaskCard() {
    if (ids.length === 0 || elements.length === 0) {
      return;
    }

    const containerId = ids[0];

    const currentAmount = Number.parseInt(sessionStorage.getItem(containerId) || "0");
    if (currentAmount >= maxAllowedTasksInPlan) {
      context.showUpgradeModal?.("gantt");
      return;
    }

    const baseTaskPosition = getBaseTaskPosition(elements[0]);
    if (!baseTaskPosition) {
      return;
    }
    const { rowId, color, splitId } = baseTaskPosition;

    const fromDate = cleanDate(elements[0].startDate).getTime();
    const toDate = cleanDate(addDays(fromDate, 1)).getTime();
    const title = "My New Task";
    const fieldValues = {
      [splitId]: rowId,
    };

    let taskId: string;
    let element: SupportedGanttElements;
    let taskType: "task" | "taskCard";

    if (useNewTaskElement) {
      // Create new task element
      const taskElement = placeTask({ x: 0, y: 0 }, {});
      taskElement.task.fromDate = fromDate;
      taskElement.task.toDate = toDate;
      taskElement.task.title = title;
      taskElement.task.containerId = containerId;
      taskElement.task.fieldValues = fieldValues;
      taskElement.task.fill = color;

      taskId = `${taskElement.task.type}-${taskElement.id}`;
      element = taskElement.task;
      taskType = "task";
    } else {
      // Create old taskCard element
      const taskCardElement = placeTaskCard({ x: 0, y: 0 });
      taskCardElement.card.fromDate = fromDate;
      taskCardElement.card.toDate = toDate;
      taskCardElement.card.title = title;
      taskCardElement.card.containerId = containerId;
      taskCardElement.card.fieldValues = fieldValues;
      taskCardElement.card.color = color; // Note: old taskCard uses 'color'

      taskId = `${taskCardElement.card.type}-${taskCardElement.id}`;
      element = taskCardElement.card;
      taskType = "taskCard";
    }

    undoRedoStack?.addAction({
      do: () => {
        reflect?.mutate.putElement({ id: taskId, element });
      },
      undo: () => {
        reflect?.mutate.deleteElement(taskId);
      },
    });

    trackGanttEvent("gantt_task_created", {
      from: "context-menu",
      amount: Number.parseInt(sessionStorage.getItem(containerId) || "0") + 1,
      type: taskType,
    });
  }

  const actualContent = (
    <>
      <IntegrationButton ids={ids} context={context} elements={elements} />
      {canGanttMondaySplitRows && <CustomRows ids={ids} context={context} elements={elements} />}
      <ButtonGroup>
        <div className={style.container}>
          <DropdownButton renderPopup={renderGranularityPicker}>{renderGranularityTitle()}</DropdownButton>
        </div>
      </ButtonGroup>
      <ButtonGroup>
        <div className={style.container}>
          <DropdownButton
            onClick={() => {
              datePickerRef.current?.focus();
            }}
            renderPopup={() => renderPicker()}
          >
            {renderDateRange()}
          </DropdownButton>
        </div>
      </ButtonGroup>
      <ButtonGroup>
        <div className={style.container}>
          <button
            className={classNames(style.addTaskButton, newStyle && style.addTaskButtonCIM)}
            onClick={createTaskCard}
          >
            {newStyle ? <Icon icon="icon-plus-button-add-task" /> : <ToolbarGanttAddTaskIcon />}
            Add Task
          </button>
        </div>
      </ButtonGroup>
    </>
  );

  if (newStyle) {
    return (
      <div className={classNames(newStyle && style.cimStyle, newStyle && style.withSeparators)}>{actualContent} </div>
    );
  } else {
    return <>{actualContent}</>;
  }
}
